import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ChevronDown, ThumbsUp } from 'lucide-react';
import { useState } from 'react';
import type { Feedback } from '../api/feedbackApi';
import { AnswerQuote, Rater } from './Rater';

function HelpfulRow({ f, first }: { f: Feedback; first: boolean }) {
  const [open, setOpen] = useState(false);
  const panel = `helpful-${f.id}`;
  return (
    <Box component="li" sx={{ borderTop: first ? 0 : 1, borderColor: 'divider', px: 2, py: 1.5 }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ color: 'market.up', display: 'flex', flexShrink: 0 }} aria-hidden><ThumbsUp size={15} /></Box>
        <Stack sx={{ flexGrow: 1, minWidth: 0, gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.question}</Typography>
          <Rater f={f} />
        </Stack>
        <ButtonBase aria-expanded={open} aria-controls={panel} onClick={() => { setOpen((v) => !v); }} sx={toggleSx(open)}>
          {open ? 'Hide answer' : 'Show answer'} <ChevronDown className="chev" size={14} aria-hidden />
        </ButtonBase>
      </Stack>
      <Collapse in={open} id={panel}>
        <Box sx={{ pt: 1.25, pl: { sm: 3.5 } }}><AnswerQuote text={f.answer} /></Box>
      </Collapse>
    </Box>
  );
}

/** Answers RMs found helpful, each one opening to the answer they got. */
export function HelpfulList({ items, title }: { items: Feedback[]; title?: string | undefined }) {
  if (items.length === 0) return null;
  return (
    <Stack component="section" aria-label={title ?? 'Rated helpful'} sx={{ gap: 1.25 }}>
      {title ? <Typography variant="h4" component="h2">{title}</Typography> : null}
      <Box component="ul" sx={listSx}>
        {items.map((f, i) => <HelpfulRow key={f.id} f={f} first={i === 0} />)}
      </Box>
    </Stack>
  );
}

const listSx = {
  listStyle: 'none',
  m: 0,
  p: 0,
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1.5,
  overflow: 'hidden',
} as const;

const toggleSx = (open: boolean) => ({
  gap: 0.5,
  flexShrink: 0,
  px: 1,
  py: 0.5,
  borderRadius: 1,
  fontSize: '0.75rem',
  color: 'text.secondary',
  '& .chev': { transition: 'transform 200ms', transform: open ? 'rotate(180deg)' : 'none' },
  '&:hover': { color: 'text.primary', bgcolor: 'surface.sidebar' },
});
